import React, { useState } from "react"
import { toAbsoluteUrl } from "@common-metronic/helpers"
import MyLoadingOverlay from "../../../../common/components/MyLoadingOverlay"
import { usePersonnelListingContext } from "./PersonnelListing"
import { usePersonnelListingListContext } from "./_List"
import { getSelectedItemPlusPreviousAndNextItem } from "./core/_helpers"
import { toast } from "react-toastify"
import MyTooltip from "@common-utils/MyTooltip"
import { IGetPersonnelListingItemResponseModel, IGetPersonnelListingResponseModel } from "./core/_models"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../../../AppContext"
import useUser from "../../../hooks/useUser"
import { CONST_USER_ROLE_IT, CONST_USER_ROLE_SUPER_ADMIN } from "@common-constants/*"
import { useThemeMode } from "@common-metronic/partials"
import moment from "moment"
import swal from "sweetalert2"
import * as Yup from "yup"
import { Form, Formik, FormikHelpers } from "formik"
import { useQueryClient } from "react-query"
import { AxiosError } from "axios"
import { IHttpErrorResponseModel, IUserModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR } from "@common-constants/*"
import MyModal from "@common-utils/MyModal"
import MyAlert from "@common-utils/MyAlert"
import MyDateDayMonthYearHoursMinutesField from "@common-utils/fields/MyDateDayMonthYearHoursMinutesField"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { personnelDisableAccountRequest, personnelEnableAccountRequest } from "../core/_requests"
import SortieDesEffectifsModal from "./SortieDesEffectifsModal"
import ChangePasswordModal from "../ChangePasswordModal"

const ListItem = ({ item, handleClickItemForPreview, isItemSelected, isItemLoading }: IListItemPropsModel) => {
     const globalContext = usePersonnelListingContext()
     const listContext = usePersonnelListingListContext()
     const queryClient = useQueryClient()
     const navigate = useNavigate()
     const { currentUser } = useAuth()
     const { hasFullStaffManagement } = useUser()
     const [isAvatarLoading, setIsAvatarLoading] = useState<boolean>(true)
     const [isGoingToBlockAccess, setIsGoingToBlockAccess] = useState<boolean>(false)
     const [isGoingToSortie, setIsGoingToSortie] = useState<boolean>(false)
     const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false)
     const { mode } = useThemeMode()

     // Après une sortie des effectifs : marque dateSortie (masque le bouton + affiche "Sorti le") et
     // reflète la coupure d'accès (dateFinAccess) dans le cache du listing.
     function updateSortieInCache(dateSortie: string) {
          const d = moment(dateSortie)
          queryClient.setQueryData<IGetPersonnelListingResponseModel | undefined>(globalContext.REACT_QUERY_KEY_GET_LISTING, prev => {
               if (!prev) return prev
               return {
                    ...prev,
                    data: prev.data.map(it =>
                         it.personnel.id === item.personnel.id
                              ? { ...it, personnel: { ...it.personnel, dateSortie: d }, user: { ...(it.user as IUserModel), dateFinAccess: d } }
                              : it
                    ),
               }
          })
     }

     // Met à jour dateFinAccess de l'item dans le cache du listing pour que le bouton bascule sans refetch
     function updateAccessInCache(newDateFinAccess: moment.Moment | undefined) {
          queryClient.setQueryData<IGetPersonnelListingResponseModel | undefined>(globalContext.REACT_QUERY_KEY_GET_LISTING, prev => {
               if (!prev) return prev
               return {
                    ...prev,
                    data: prev.data.map(it =>
                         it.personnel.id === item.personnel.id ? { ...it, user: { ...(it.user as IUserModel), dateFinAccess: newDateFinAccess } } : it
                    ),
               }
          })
     }

     // Réactive le compte (ou annule une désactivation programmée) — identique à l'action de la page détails
     function enableAccount(e: React.MouseEvent) {
          e.stopPropagation()
          swal.fire({
               icon: "warning",
               text: item.user?.dateFinAccess?.isAfter(moment())
                    ? `Le compte est prévu pour être désactivé le ${item.user?.dateFinAccess?.format("dddd D MMMM YYYY à H[h]mmm")}. Désirez-vous annuler cette opération?`
                    : `Voulez-vous vraiment réactiver ce compte ?`,
               showCancelButton: true,
               confirmButtonText: "Oui",
               cancelButtonText: "Annuler",
               focusConfirm: true,
               showLoaderOnConfirm: true,
               preConfirm: async () => {
                    return personnelEnableAccountRequest(item.personnel.id as number)
                         .then(() => {
                              updateAccessInCache(undefined)
                              toast.success("Compte réactivé avec succès.")
                         })
                         .catch(err => {
                              toast.error(err?.response?.data?.detail, { autoClose: false })
                         })
               },
               allowOutsideClick: () => !swal.isLoading(),
          }).then()
     }

     // Modal de saisie de la date de fin d'accès (désactivation) — identique à l'action de la page détails
     const FormDisableAccessInModal = () => {
          interface IPersonnelDisableAccessRequestModel {
               dateFinAccess?: string
          }

          const schema = Yup.object().shape({
               dateFinAccess: Yup.string().required().label(`"Date de fin d'accès"`),
          })

          const initialValues: IPersonnelDisableAccessRequestModel = {
               dateFinAccess: moment().format("YYYY-MM-DDTHH:mm"),
          }

          function handleSubmit(values: IPersonnelDisableAccessRequestModel, helpers: FormikHelpers<IPersonnelDisableAccessRequestModel>) {
               helpers.setStatus(null)

               personnelDisableAccountRequest(item.personnel.id as number, values.dateFinAccess as string)
                    .then(() => {
                         updateAccessInCache(moment(values.dateFinAccess))
                         toast.success(
                              moment(values.dateFinAccess).isAfter(moment())
                                   ? `Les accès seront coupés à partir du ${moment(values.dateFinAccess).format("dddd D MMMM YYYY")}, soit ${moment(
                                          values.dateFinAccess
                                     ).fromNow()}`
                                   : "Les accès ont été coupés avec succès."
                         )
                         setIsGoingToBlockAccess(false)
                    })
                    .catch((e: AxiosError) => {
                         const error: IHttpErrorResponseModel = e.response?.data

                         if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                              for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                         }

                         helpers.setStatus(error?.detail)
                         toast.error(error?.detail, { autoClose: false })
                         helpers.setSubmitting(false)
                    })
          }

          return (
               <MyModal show={true} onCloseCallback={() => setIsGoingToBlockAccess(false)} title={<>Renseigner la date de fin d'accès</>}>
                    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                         {helpers => (
                              <Form noValidate autoComplete="off">
                                   {helpers.status && (
                                        <MyAlert type={"danger"} classNames={"mb-4"}>
                                             {helpers.status}
                                        </MyAlert>
                                   )}

                                   <MyDateDayMonthYearHoursMinutesField
                                        name={"dateFinAccess"}
                                        value={helpers.values.dateFinAccess}
                                        placeholder={"Date de fin d'accès"}
                                        onChange={val => helpers.setFieldValue("dateFinAccess", val)}
                                        minDate={moment().format("YYYY-MM-DD HH:mm")}
                                   />

                                   <div className="d-flex justify-content-end mt-5">
                                        <button
                                             type={"button"}
                                             className={"btn btn-outline btn-active-secondary btn-sm me-2"}
                                             onClick={() => setIsGoingToBlockAccess(false)}
                                             disabled={helpers.isSubmitting}
                                        >
                                             Annuler
                                        </button>
                                        <button type={"submit"} className={"btn btn-primary btn-sm"} disabled={helpers.isSubmitting}>
                                             {!helpers.isSubmitting ? <>Confirmer</> : <MySimpleSpinner size={"sm"} />}
                                        </button>
                                   </div>
                              </Form>
                         )}
                    </Formik>
               </MyModal>
          )
     }

     function handleOnCheckItem() {
          if (!globalContext.isBulkSelectionEnabled.checkedElementsInPage.includes(item.personnel.id as number)) {
               globalContext.setIsBulkSelectionEnabled(prev => ({
                    ...prev,
                    checkedElementsInPage: [...prev.checkedElementsInPage, item.personnel.id as number],
                    areAllElementsInAllPagesChecked: false,
               }))
          } else {
               globalContext.setIsBulkSelectionEnabled(prev => ({
                    ...prev,
                    checkedElementsInPage: prev.checkedElementsInPage.filter(id => id !== item.personnel.id),
                    areAllElementsInAllPagesChecked: false,
               }))
          }
     }

     return (
          <MyLoadingOverlay isActive={isItemLoading}>
               <>
                    <div
                         className={`${isItemSelected ? "border-start border-2 border-primary" : ""}`}
                         onClick={e => handleClickItemForPreview(e)}
                         ref={isItemSelected ? listContext.selectedItemRef : null}
                    >
                         <div className={`p-4 ${isItemSelected ? "mb-4 bg-light" : "cursor-pointer"}`}>
                              {/* checkbox, Avatar, nom & métier*/}
                              <div className={"d-flex justify-content-between"}>
                                   {/* checkbox */}
                                   {globalContext.isBulkSelectionEnabled.enabled && (
                                        <div className={"form-check form-check-custom form-check-solid form-check-sm me-4"}>
                                             <input
                                                  type="checkbox"
                                                  className={"form-check-input cursor-pointer border border-secondary not-concerned-by-preview"}
                                                  checked={globalContext.isBulkSelectionEnabled.checkedElementsInPage.includes(item.personnel.id as number)}
                                                  onChange={() => handleOnCheckItem()}
                                             />
                                        </div>
                                   )}

                                   {/* Avatar, titre */}
                                   <div className={"d-flex align-items-center"}>
                                        {/* Avatar */}
                                        <MyLoadingOverlay isActive={isAvatarLoading} styles={{ height: "50px", width: "50px", borderRadius: "50%" }} size={"sm"}>
                                             <div className={"d-flex"}>
                                                  <img
                                                       src={
                                                            item.personnel?.avatarGoogleDriveId
                                                                 ? FileAndDriveHelper.getThumbnailDriveUrl(item.personnel?.avatarGoogleDriveId)
                                                                 : toAbsoluteUrl(`/media/svg/avatars/${mode === "light" ? "blank" : "blank-dark"}.svg`)
                                                       }
                                                       onLoad={() => setIsAvatarLoading(false)}
                                                       style={{ height: "50px", width: "50px", borderRadius: "50%" }}
                                                       alt="Avatar"
                                                  />
                                             </div>
                                        </MyLoadingOverlay>

                                        {/* Titre */}
                                        <div className={"ms-4"}>
                                             <div className={"d-flex"}>
                                                  <Link
                                                       to={`/gestion/structure/staff/${item.personnel.id}/details?nom=${item.personnel.prenomNom}`}
                                                       className={"fw-bold text-hover-dark fs-5"}
                                                  >
                                                       {item.personnel.prenomNom}
                                                  </Link>
                                             </div>
                                             <div className={"text-muted"}>
                                                  {item?.personnel.personnelPostes && item?.personnel.personnelPostes?.length > 0
                                                       ? item?.personnel.personnelPostes[0]?.poste?.nom
                                                       : "Poste non renseigné"}
                                             </div>
                                        </div>
                                   </div>

                                   {/* Right side */}
                                   <div className="d-flex align-items-center">
                                        <span className={"badge badge-dark me-1"}>{item.personnel.mainEntite?.nom}</span>
                                        <span className={"badge badge-dark"}>
                                             {!item?.personnel?.statut && "Statut non renseigné"}
                                             {item?.personnel?.statut && item?.personnel?.statut === "STATUT_NON_SALARIE" ? "Non salarié" : item.personnel.statutLabel}
                                        </span>
                                   </div>
                              </div>
                         </div>

                         {isItemSelected && (
                              <>
                                   <div className={"d-flex align-items-center px-4 pb-4"}>
                                        <div className={"col-10"}>
                                             <div className={`d-flex align-items-center mb-2`}>
                                                  <span className="fas fa-envelope fs-5 w-25px" />
                                                  <a href={`mailto:${item.personnel.mailProfessionnel}`}>{item.personnel.mailProfessionnel}</a>
                                             </div>

                                             <div className={`d-flex align-items-center mb-2`}>
                                                  <span className="fas fa-phone fs-5 w-25px" />
                                                  <a href={`tel:${item.personnel.telProfessionnelIndicatif?.phoneCodeWithPlusPrefix} ${item.personnel.telProfessionnel}`}>
                                                       {item.personnel.telProfessionnelIndicatif?.phoneCodeWithPlusPrefix} {item.personnel.telProfessionnel}
                                                  </a>
                                             </div>

                                             {item.responsable && (
                                                  <div className={`d-flex align-items-center mb-2`}>
                                                       <span className="fas fa-user fs-5 w-25px" />
                                                       Sous la responsabilité de {item.responsable?.prenomNom}
                                                  </div>
                                             )}

                                             <div className={`d-flex align-items-center mb-2`}>
                                                  <span className="fas fa-building fs-5 w-25px" />
                                                  {item.personnel.agence?.nom}
                                             </div>

                                             <div className={`d-flex align-items-center mb-2`}>
                                                  <span className="fas fa-calendar-check fs-5 w-25px" />
                                                  {item.personnel.dateEntree?.format("[Entré le] dddd D MMMM YYYY")}
                                             </div>

                                             {item.personnel.dateSortie && (
                                                  <div className={`d-flex align-items-center mb-2`}>
                                                       <span className="fas fa-calendar-xmark fs-5 w-25px" />
                                                       {item.personnel.dateSortie?.format("[Sorti le] dddd D MMMM YYYY")}
                                                  </div>
                                             )}
                                        </div>
                                        <div className="col text-end">
                                             {/* Actions sensibles réservées à l'accès complet (masquées en accès restreint) */}
                                             {hasFullStaffManagement() && (
                                                  <>
                                             <MyTooltip title={"Changer le mot de passe"} placement={"top"}>
                                                  <div>
                                                       <button
                                                            type={"button"}
                                                            className={`btn btn-outline btn-outline-primary btn-sm btn-icon rounded-circle mb-2 not-concerned-by-preview`}
                                                            onClick={e => {
                                                                 e.stopPropagation()
                                                                 setIsChangingPassword(true)
                                                            }}
                                                       >
                                                            <i className="fas fa-key fs-4 not-concerned-by-preview" />
                                                       </button>
                                                  </div>
                                             </MyTooltip>
                                             {!item.user?.dateFinAccess ? (
                                                  <MyTooltip title={"Désactiver compte"} placement={"top"}>
                                                       <div>
                                                            <button
                                                                 type={"button"}
                                                                 className={`btn btn-outline btn-outline-danger btn-sm btn-icon rounded-circle mb-2 not-concerned-by-preview`}
                                                                 onClick={e => {
                                                                      e.stopPropagation()
                                                                      setIsGoingToBlockAccess(true)
                                                                 }}
                                                            >
                                                                 <i className="fas fa-power-off fs-4 not-concerned-by-preview" />
                                                            </button>
                                                       </div>
                                                  </MyTooltip>
                                             ) : (
                                                  <MyTooltip title={"Activer compte"} placement={"top"}>
                                                       <div>
                                                            <button
                                                                 type={"button"}
                                                                 className={`btn btn-outline btn-outline-success btn-sm btn-icon rounded-circle mb-2 not-concerned-by-preview`}
                                                                 onClick={e => enableAccount(e)}
                                                            >
                                                                 <i className="fas fa-power-off fs-4 not-concerned-by-preview" />
                                                            </button>
                                                       </div>
                                                  </MyTooltip>
                                             )}
                                             {!item.personnel.dateSortie && (
                                                  <MyTooltip title={"Sortir des effectifs"} placement={"bottom"}>
                                                       <button
                                                            type={"button"}
                                                            className={`btn btn-outline btn-outline-warning btn-sm btn-icon rounded-circle mb-2 not-concerned-by-preview`}
                                                            onClick={e => {
                                                                 e.stopPropagation()
                                                                 setIsGoingToSortie(true)
                                                            }}
                                                       >
                                                            <i className="fas fa-door-open fs-4 not-concerned-by-preview" />
                                                       </button>
                                                  </MyTooltip>
                                             )}
                                                  </>
                                             )}
                                        </div>
                                   </div>

                                   {isGoingToBlockAccess && <FormDisableAccessInModal />}
                                   {isGoingToSortie && (
                                        <SortieDesEffectifsModal
                                             personnelId={item.personnel.id as number}
                                             personnelNom={item.personnel.prenomNom as string}
                                             onClose={() => setIsGoingToSortie(false)}
                                             onDone={dateSortie => {
                                                  updateSortieInCache(dateSortie)
                                                  // Après archivage : les super-admin / IT sont dirigés vers la checklist post-archive
                                                  if (currentUser?.roles?.includes(CONST_USER_ROLE_SUPER_ADMIN) || currentUser?.roles?.includes(CONST_USER_ROLE_IT))
                                                       navigate("/personnel/taches-post-archive")
                                             }}
                                        />
                                   )}
                                   {isChangingPassword && (
                                        <ChangePasswordModal
                                             personnelId={item.personnel.id as number}
                                             personnelNom={item.personnel.prenomNom as string}
                                             onClose={() => setIsChangingPassword(false)}
                                        />
                                   )}
                              </>
                         )}
                    </div>
               </>
          </MyLoadingOverlay>
     )
}

const ListingItemWrapper = ({ item, index }: { item: IGetPersonnelListingItemResponseModel; index: number }) => {
     const context = usePersonnelListingContext()

     const isItemSelected: boolean = context.itemInPreview !== null && context.itemInPreview.selectedItem.personnel.id === item.personnel.id
     const isItemLoading: boolean =
          context.itemInPreview !== null && context.itemInPreview.selectedItem.personnel.id === item.personnel.id && context.itemInPreview.isSelectedItemLoading

     // Shows item in preview
     function handleClickItemForPreview(e: React.MouseEvent<any>) {
          const clickedElement = e.target as HTMLElement
          // .not-concerned-by-preview elements are not concerned by the click
          if (!clickedElement.classList.contains("not-concerned-by-preview") && context.itemInPreview?.selectedItem.personnel.id !== item.personnel.id) {
               context.setAreFiltersVisible(false)

               context.setItemInPreview(
                    getSelectedItemPlusPreviousAndNextItem(item, index, context.listingQuery.data!.data, context.listingQuery.data!.page, context.listingQuery.data!.totalPages)
               )
          }
     }

     return <ListItem item={item} handleClickItemForPreview={handleClickItemForPreview} isItemSelected={isItemSelected} isItemLoading={isItemLoading} />
}

interface IListItemPropsModel {
     item: IGetPersonnelListingItemResponseModel
     handleClickItemForPreview: (e: React.MouseEvent<any>) => void
     isItemSelected: boolean
     isItemLoading: boolean
}

export default ListingItemWrapper
